/**
 * bomSpreadsheetTypes.ts
 *
 * Type definitions and helper constructors for the unified BOM Spreadsheet & Configuration Matrix.
 */

export interface BomSpreadsheetWoodRow {
  id?: string
  part_name: string
  wood_type_raw: string
  dims_finished_t: number | string
  dims_finished_w: number | string
  dims_finished_l: number | string
  tenon: string
  qty_per: number | string
  qty_total: number | string
  notes: string
  veneer_spec: string
}

export interface BomSpreadsheetHardwareRow {
  id?: string
  name_raw: string
  spec_raw: string
  qty_per: number | string
  qty_total: number | string
  uom_hint: string
  color_hint: string
  item_type: string
  notes: string
  material_id?: string | null
  material_code?: string | null
  material_name?: string | null
  supplier_id?: string | null
  supplier_name?: string | null
  unit_price?: number | null
  vat_rate?: number | null
}

export interface BomConfigRow {
  id: string
  code: string // Displays only the product code (e.g. MP180)
  colors: string[]
  woods: string[]
  assemblies: string[]
  status: 'OK' | 'Đang Cập Nhật'
}

export interface ProductBomSpreadsheetData {
  productCode: string
  productName: string
  configCode: string
  wood: BomSpreadsheetWoodRow[]
  hardware: BomSpreadsheetHardwareRow[]
}

export interface BomRevisionDiffItem {
  type: 'cell_change' | 'row_added' | 'row_deleted'
  table: 'wood' | 'hardware'
  rowIndex: number
  rowName: string
  field?: string
  fieldLabel?: string
  oldValue?: string | number
  newValue?: string | number
}

export interface BomRevision {
  id: string
  revNumber: number
  revLabel: string // "Rev. 01", "Rev. 02"
  savedAt: string  // "23/09/2026"
  savedTime?: string // "19:15"
  author: string   // "Hà Phan Thanh Tâm"
  changesSummary: string
  diffs: BomRevisionDiffItem[]
  snapshot: {
    wood: BomSpreadsheetWoodRow[]
    hardware: BomSpreadsheetHardwareRow[]
  }
}

/**
 * Rule: "Khi tạo mới mặc định trống (trong ô ko có text)"
 * New Wood row with all empty fields.
 */
export function createBlankWoodRow(): BomSpreadsheetWoodRow {
  return {
    id: `wood-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    part_name: '',
    wood_type_raw: '',
    dims_finished_t: '',
    dims_finished_w: '',
    dims_finished_l: '',
    tenon: '',
    qty_per: '',
    qty_total: '',
    notes: '',
    veneer_spec: '',
  }
}

/**
 * Rule: "Khi tạo mới mặc định trống (trong ô ko có text)"
 * New Hardware row with all empty fields.
 */
export function createBlankHardwareRow(): BomSpreadsheetHardwareRow {
  return {
    id: `hw-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name_raw: '',
    spec_raw: '',
    qty_per: '',
    qty_total: '',
    uom_hint: '',
    color_hint: '',
    item_type: '',
    notes: '',
  }
}

/**
 * Create a new BOM Configuration row with clean empty selections.
 */
export function createBlankBomConfigRow(productCode: string): BomConfigRow {
  return {
    id: `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    code: productCode,
    colors: [],
    woods: [],
    assemblies: [],
    status: 'Đang Cập Nhật',
  }
}

/**
 * Calculate row CBM in cubic meters: (T * W * L * Qty) / 1,000,000,000
 */
export function calculateWoodRowCbm(r: BomSpreadsheetWoodRow): number {
  const t = typeof r.dims_finished_t === 'number' ? r.dims_finished_t : parseFloat(r.dims_finished_t || '0')
  const w = typeof r.dims_finished_w === 'number' ? r.dims_finished_w : parseFloat(r.dims_finished_w || '0')
  const l = typeof r.dims_finished_l === 'number' ? r.dims_finished_l : parseFloat(r.dims_finished_l || '0')
  const qty = typeof r.qty_per === 'number' ? r.qty_per : parseFloat(r.qty_per || '0')

  if (isNaN(t) || isNaN(w) || isNaN(l) || isNaN(qty) || t <= 0 || w <= 0 || l <= 0 || qty <= 0) {
    return 0
  }
  return (t * w * l * qty) / 1_000_000_000
}
